"use client";

// ---------------------------------------------------------------------------
// GC OPTIMIZE™ Calculator — Navigation & Clinical Data
//
// This module defines all step identifiers, step metadata, ordered path
// sequences (per population + CRENESSITY status), GC type definitions, HCe
// conversion multipliers, and branching thresholds.
//
// The component reads from this object to:
//   • Build the stepper sidebar labels
//   • Determine which steps are active for the current patient
//   • Evaluate conditional step visibility via `showWhen` condition keys
//   • Compute HCe totals and apply dose-branching logic
//
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Step ID constants
// ---------------------------------------------------------------------------

export const GC_CALC_STEP = {
  PATIENT_PROFILE:           'patient-profile',
  STATUS_INFO:               'status-info',
  PATIENT_EXPECTATIONS:      'patient-expectations',
  GC_THERAPY_ADJUSTMENTS:    'gc-therapy-adjustments',
  STARTING_LABS:             'starting-labs',
  MOST_RECENT_LABS:          'most-recent-labs',
  ASSESS_TREATMENT_PROGRESS: 'assess-treatment-progress',
  CLINICAL_OPTIONS:          'clinical-options',
  SUMMARY:                   'summary',
};

// ---------------------------------------------------------------------------
// Population and status constants
// ---------------------------------------------------------------------------

export const GC_CALC_POPULATION = {
  ADULT:     'adult',
  PEDIATRIC: 'pediatric',
};

export const GC_CALC_STATUS = {
  STARTING:   'starting',   // "Starting a patient"
  CONTINUING: 'continuing', // "Patient already on CRENESSITY"
};

// ---------------------------------------------------------------------------
// HCe conversion multipliers
// Ref: hydrocortisone ×1, prednisolone/prednisone ×4,
//      methylprednisolone ×5, dexamethasone ~×60
// ---------------------------------------------------------------------------

export const HCE_MULTIPLIERS = {
  hydrocortisone:                   1,
  prednisolonePrednisone:           4,
  prednisolonePrednisonePediatric:  5,
  methylprednisolone:               5,
  methylprednisolonePediatric:      6,
  dexamethasone:                    60,
};

// ---------------------------------------------------------------------------
// GC type definitions
// id must be stable — used as form field keys and reduction schedule keys
// ---------------------------------------------------------------------------

export const GC_TYPES = [
  {
    id:                  'hydrocortisone',
    label:               'Hydrocortisone',
    multiplier:          HCE_MULTIPLIERS.hydrocortisone,
    placeholder:         'Eg, 30',
    pediatricPlaceholder: 'Eg, 20',
    recommendedReduction: {
      adult: {
        unitType: 'mg',
        aboveHigh: {
          min: 5,
          max: 10,
        },
        belowOrEqualHigh: {
          min: 2.5,
          max: 5,
        },
      },
      pediatric: {
        unitType: 'percentage',
        min: .1,
        max: .2,
      }
    },
  },
  {
    id:                  'prednisolone-prednisone',
    label:               'Prednisolone/ Prednisone',
    multiplier:          HCE_MULTIPLIERS.prednisolonePrednisone,
    pediatricMultiplier: HCE_MULTIPLIERS.prednisolonePrednisonePediatric,
    placeholder:         'Eg, 7',
    pediatricPlaceholder: 'Eg, 4',
    recommendedReduction: {
      adult: {
        unitType: 'mg',
        aboveHigh: {
          min: 1.25,
          max: 2.5,
        },
        belowOrEqualHigh: {
          min: 0.63,
          max: 1.25,
        },
      },
      pediatric: {
        unitType: 'percentage',
        min: .1,
        max: .2,
      }
    },
  },
  {
    id:                  'methylprednisolone',
    label:               'Methylprednisolone',
    multiplier:          HCE_MULTIPLIERS.methylprednisolone,
    pediatricMultiplier: HCE_MULTIPLIERS.methylprednisolonePediatric,
    placeholder:         'Eg, 6',
    pediatricPlaceholder: 'Eg, 3',
    recommendedReduction: {
      adult: {
        unitType: 'mg',
        aboveHigh: {
          min: 1,
          max: 2,
        },
        belowOrEqualHigh: {
          min: 0.5,
          max: 1,
        },
      },
      pediatric: {
        unitType: 'percentage',
        min: .1,
        max: .2,
      }
    },
  },
  {
    id:                  'dexamethasone',
    label:               'Dexamethasone',
    multiplier:          HCE_MULTIPLIERS.dexamethasone,
    placeholder:         'Eg, 0.5',
    pediatricPlaceholder: 'Eg, 0.3',
    recommendedReduction: {
      adult: {
        unitType: 'mg',
        aboveHigh: {
          min: 0.08,
          max: 0.17,
        },
        belowOrEqualHigh: {
          min: 0.04,
          max: 0.08,
        },
      },
      pediatric: {
        unitType: 'percentage',
        min: .1,
        max: .2,
      }
    },
  },
];

// ---------------------------------------------------------------------------
// HCe dose thresholds for adult branching logic
//
//   hce >= HCE_THRESHOLDS.HIGH       → no treatment-progress step
//   hce <= HCE_THRESHOLDS.WITHDRAWAL → eligible for treatment-progress step
//   HCE_THRESHOLDS.MID_LOW <= hce <= HCE_THRESHOLDS.WITHDRAWAL → mid branch
//   hce < HCE_THRESHOLDS.MID_LOW     → low branch
// ---------------------------------------------------------------------------

export const HCE_THRESHOLDS = {
  HIGH:       51, // ≥51 mg/day → no treatment-progress step; reduction 5–10 mg
  WITHDRAWAL: 50, // ≤50 mg/day → eligible for assess-treatment-progress step
  MID_LOW:    25, // 25–50 mg/day → reduction 2.5–5 mg (mid branch)
               // <25 mg/day → reduction 2.5–5 mg + adrenal insufficiency note (low branch)
};

// ---------------------------------------------------------------------------
// Pediatric supraphysiologic dose threshold (mg/m²/day)
// Source: CAHtalyst® Pediatric study definition
// ---------------------------------------------------------------------------

export const PEDIATRIC_SUPRAPHYSIOLOGIC_THRESHOLD = 11; // >11 mg/m²/day

// ---------------------------------------------------------------------------
// Condition keys — referenced by `showWhen` in path step entries.
// The component evaluates these against current form state.
//
// Convention: `state` is the full calculator form state object.
// ---------------------------------------------------------------------------

export const GC_CALC_CONDITION = {
  // Adult: assess-treatment-progress shown only if previously down-titrated
  HCE_LTE_50_AND_DOWN_TITRATED_BEFORE: 'hce_lte_50_and_down_titrated_before',

  // Pediatric: gc-therapy-adjustments path (A4 ≤ goal AND supraphysiologic dose)
  // Also true when a4Assessment not yet set (disabled preview before Starting Labs)
  PEDS_A4_GC_REDUCTION:  'peds_a4_gc_reduction',

  // Pediatric: A4 confirmed on reduction branch (after Starting Labs saves)
  PEDS_A4_CONFIRMED_REDUCTION: 'peds_a4_confirmed_reduction',

  // Pediatric: clinical-options path (A4 > goal OR A4 ~ goal)
  PEDS_A4_CLINICAL_OPT:  'peds_a4_clinical_options',

  // Pediatric: GC therapy step has been saved (gcSelectedTypes populated)
  PEDS_GC_THERAPY_DONE:  'peds_gc_therapy_done',

  // Pediatric: Clinical Options ready (A4 > goal direct path OR GC therapy done)
  PEDS_CLINICAL_OPTIONS_READY: 'peds_clinical_options_ready',
};

// ---------------------------------------------------------------------------
// Step definitions
//
// population: 'adult' | 'pediatric' | 'both'
// path:       'starting' | 'continuing' | 'both'
// stepType:   'form'    — collects user input
//             'info'    — displays guidance; no required fields
//             'branch'  — evaluates a condition and routes to next step
//             'summary' — final email / summary screen
// ---------------------------------------------------------------------------

export const GC_CALC_STEP_DEFINITIONS = {
  [GC_CALC_STEP.PATIENT_PROFILE]: {
    id:    GC_CALC_STEP.PATIENT_PROFILE,
    label: 'Patient Profile',
  },

  [GC_CALC_STEP.STATUS_INFO]: {
    id:        GC_CALC_STEP.STATUS_INFO,
    label:     'Status Info',
    transient: true,  // not rendered as a pill in the step list
  },

  [GC_CALC_STEP.PATIENT_EXPECTATIONS]: {
    id:    GC_CALC_STEP.PATIENT_EXPECTATIONS,
    label: 'Patient Expectations',
  },

  [GC_CALC_STEP.GC_THERAPY_ADJUSTMENTS]: {
    id:    GC_CALC_STEP.GC_THERAPY_ADJUSTMENTS,
    label: 'GC Therapy & Adjustments',
  },

  [GC_CALC_STEP.STARTING_LABS]: {
    id:    GC_CALC_STEP.STARTING_LABS,
    label: 'Starting Labs',
  },

  [GC_CALC_STEP.MOST_RECENT_LABS]: {
    id:    GC_CALC_STEP.MOST_RECENT_LABS,
    label: 'Most Recent Labs',
  },

  [GC_CALC_STEP.ASSESS_TREATMENT_PROGRESS]: {
    id:    GC_CALC_STEP.ASSESS_TREATMENT_PROGRESS,
    label: 'Assess Treatment Progress',
  },

  [GC_CALC_STEP.CLINICAL_OPTIONS]: {
    id:    GC_CALC_STEP.CLINICAL_OPTIONS,
    label: 'Clinical Options',
  },

  [GC_CALC_STEP.SUMMARY]: {
    id:    GC_CALC_STEP.SUMMARY,
    label: 'Summary',
  },
};

// ---------------------------------------------------------------------------
// Ordered path sequences
//
// Each entry is: { stepId, showWhen? }
//   showWhen — optional condition key from GC_CALC_CONDITION; when present
//              the component evaluates it against form state before including
//              the step in the active sequence.
//
// Some steps are conditionally included with showWhen evaluators,
// including adult and pediatric assess-treatment-progress branches.
// ---------------------------------------------------------------------------

export const GC_CALC_PATHS = {
  [GC_CALC_POPULATION.ADULT]: {
    [GC_CALC_STATUS.STARTING]: [
      { stepId: GC_CALC_STEP.PATIENT_PROFILE },
      { stepId: GC_CALC_STEP.STATUS_INFO },
      { stepId: GC_CALC_STEP.PATIENT_EXPECTATIONS },
      { stepId: GC_CALC_STEP.GC_THERAPY_ADJUSTMENTS },
      { stepId: GC_CALC_STEP.CLINICAL_OPTIONS },
      { stepId: GC_CALC_STEP.ASSESS_TREATMENT_PROGRESS, showWhen: GC_CALC_CONDITION.HCE_LTE_50_AND_DOWN_TITRATED_BEFORE },
      { stepId: GC_CALC_STEP.SUMMARY },
    ],
    [GC_CALC_STATUS.CONTINUING]: [
      { stepId: GC_CALC_STEP.PATIENT_PROFILE },
      { stepId: GC_CALC_STEP.STATUS_INFO },
      { stepId: GC_CALC_STEP.PATIENT_EXPECTATIONS },
      { stepId: GC_CALC_STEP.GC_THERAPY_ADJUSTMENTS },
      { stepId: GC_CALC_STEP.CLINICAL_OPTIONS },
      { stepId: GC_CALC_STEP.ASSESS_TREATMENT_PROGRESS, showWhen: GC_CALC_CONDITION.HCE_LTE_50_AND_DOWN_TITRATED_BEFORE },
      { stepId: GC_CALC_STEP.SUMMARY },
    ],
  },

  [GC_CALC_POPULATION.PEDIATRIC]: {
    [GC_CALC_STATUS.STARTING]: [
      { stepId: GC_CALC_STEP.PATIENT_PROFILE },
      { stepId: GC_CALC_STEP.STATUS_INFO },
      { stepId: GC_CALC_STEP.PATIENT_EXPECTATIONS },
      { stepId: GC_CALC_STEP.STARTING_LABS },
      // ── A4 > goal path: Assess Treatment Progress first, then Clinical Options
      { stepId: GC_CALC_STEP.ASSESS_TREATMENT_PROGRESS, showWhen: GC_CALC_CONDITION.PEDS_A4_CLINICAL_OPT },
      { stepId: GC_CALC_STEP.CLINICAL_OPTIONS,          showWhen: GC_CALC_CONDITION.PEDS_A4_CLINICAL_OPT },
      // ── A4 ≤ goal path: GC Therapy, then Assess Treatment Progress, then Clinical Options
      { stepId: GC_CALC_STEP.GC_THERAPY_ADJUSTMENTS,    showWhen: GC_CALC_CONDITION.PEDS_A4_GC_REDUCTION },
      { stepId: GC_CALC_STEP.ASSESS_TREATMENT_PROGRESS, showWhen: GC_CALC_CONDITION.PEDS_A4_CONFIRMED_REDUCTION },
      { stepId: GC_CALC_STEP.CLINICAL_OPTIONS,          showWhen: GC_CALC_CONDITION.PEDS_GC_THERAPY_DONE },
      // ── Summary (always last, shows once either path has reached Clinical Options)
      { stepId: GC_CALC_STEP.SUMMARY,                   showWhen: GC_CALC_CONDITION.PEDS_CLINICAL_OPTIONS_READY },
    ],
    [GC_CALC_STATUS.CONTINUING]: [
      { stepId: GC_CALC_STEP.PATIENT_PROFILE },
      { stepId: GC_CALC_STEP.STATUS_INFO },
      { stepId: GC_CALC_STEP.PATIENT_EXPECTATIONS },
      { stepId: GC_CALC_STEP.MOST_RECENT_LABS },
      // ── A4 ~ goal or A4 > goal path: Assess Treatment Progress, then Clinical Options
      { stepId: GC_CALC_STEP.ASSESS_TREATMENT_PROGRESS, showWhen: GC_CALC_CONDITION.PEDS_A4_CLINICAL_OPT },
      { stepId: GC_CALC_STEP.CLINICAL_OPTIONS,          showWhen: GC_CALC_CONDITION.PEDS_A4_CLINICAL_OPT },
      // ── A4 < goal path: GC Therapy → Assess Treatment Progress → Clinical Options
      { stepId: GC_CALC_STEP.GC_THERAPY_ADJUSTMENTS,    showWhen: GC_CALC_CONDITION.PEDS_A4_GC_REDUCTION },
      { stepId: GC_CALC_STEP.ASSESS_TREATMENT_PROGRESS, showWhen: GC_CALC_CONDITION.PEDS_A4_CONFIRMED_REDUCTION },
      { stepId: GC_CALC_STEP.CLINICAL_OPTIONS,          showWhen: GC_CALC_CONDITION.PEDS_A4_CONFIRMED_REDUCTION },
      { stepId: GC_CALC_STEP.SUMMARY },
    ],
  },
};

// ---------------------------------------------------------------------------
// Condition evaluators
//
// Maps each GC_CALC_CONDITION key to a pure function: (state) => boolean
// `state` is the full calculator form state object maintained by the component.
//
// Import and call these at the component level — do not inline in JSX.
// ---------------------------------------------------------------------------

export const GC_CALC_CONDITION_EVALUATORS = {
  [GC_CALC_CONDITION.HCE_LTE_50_AND_DOWN_TITRATED_BEFORE]: (state) =>
    (typeof state.hceTotal === 'number' && state.hceTotal <= HCE_THRESHOLDS.WITHDRAWAL) &&
    state.formValues?.gcDownTitrationExperience === 'down-titrated-before',

  [GC_CALC_CONDITION.PEDS_A4_CLINICAL_OPT]: (state) =>
    state.formValues?.a4Assessment === 'above-goal' || state.formValues?.a4Assessment === 'at-goal',

  // Show when A4 assessment not yet made (disabled preview) OR A4 ≤ goal + supraphysiologic
  [GC_CALC_CONDITION.PEDS_A4_GC_REDUCTION]: (state) =>
    !state.formValues?.a4Assessment ||
    state.formValues?.a4Assessment === 'below-goal' ||
    state.formValues?.a4Assessment === 'at-goal-supraphysiologic',

  // True only when a4Assessment is definitively set to a reduction value
  [GC_CALC_CONDITION.PEDS_A4_CONFIRMED_REDUCTION]: (state) =>
    state.formValues?.a4Assessment === 'below-goal' ||
    state.formValues?.a4Assessment === 'at-goal-supraphysiologic',

  [GC_CALC_CONDITION.PEDS_GC_THERAPY_DONE]: (state) =>
    Array.isArray(state.formValues?.gcSelectedTypes) && state.formValues.gcSelectedTypes.length > 0,

  // Clinical Options shows when: A4 > goal (direct) OR GC therapy completed (reduction path)
  [GC_CALC_CONDITION.PEDS_CLINICAL_OPTIONS_READY]: (state) =>
    state.formValues?.a4Assessment === 'above-goal' ||
    (Array.isArray(state.formValues?.gcSelectedTypes) && state.formValues.gcSelectedTypes.length > 0),
};

// ---------------------------------------------------------------------------
// Resolve the active step sequence for the current patient
//
// Returns the ordered array of step definition objects that apply to this
// patient, filtering out conditional steps whose condition is not yet met.
//
// Call this whenever `state.population`, `state.status`, or any branching
// condition field changes.
//
// @param {object} state - Current calculator form state
// @param {string} state.population - GC_CALC_POPULATION value
// @param {string} state.status     - GC_CALC_STATUS value
// @returns {Array<object>} Ordered array of step definitions
// ---------------------------------------------------------------------------

export function resolveActiveSteps(state) {
  const { population, status } = state;

  const pathSteps = GC_CALC_PATHS[population]?.[status];
  // Before population/status are known (Step 1 not yet saved), fall back to
  // just the Patient Profile step so the entry form is always renderable.
  if (!pathSteps) return [GC_CALC_STEP_DEFINITIONS[GC_CALC_STEP.PATIENT_PROFILE]];

  return pathSteps
    .filter(({ showWhen }) => {
      if (!showWhen) return true;
      const evaluator = GC_CALC_CONDITION_EVALUATORS[showWhen];
      return evaluator ? evaluator(state) : true;
    })
    .map(({ stepId }) => GC_CALC_STEP_DEFINITIONS[stepId]);
}

// ---------------------------------------------------------------------------
// Compute total HCe from a GC doses map
//
// @param {object} gcDoses - Map of GC type id → dose in mg(/day or /m²/day)
//   e.g. { 'hydrocortisone': 30, 'dexamethasone': 0.5 }
// @returns {number} Total HCe (rounded to 2 decimal places)
// ---------------------------------------------------------------------------

export function computeTotalHce(gcDoses = {}, population = GC_CALC_POPULATION.ADULT) {
  return GC_TYPES.reduce((total, { id, multiplier, pediatricMultiplier }) => {
    const dose = parseFloat(gcDoses[id]) || 0;
    const m = population === GC_CALC_POPULATION.PEDIATRIC && pediatricMultiplier ? pediatricMultiplier : multiplier;
    return total + dose * m;
  }, 0);
}

// ---------------------------------------------------------------------------
// Valid form-field values (single source of truth for steps + output page)
// ---------------------------------------------------------------------------

export const GC_CALC_TREATMENT_GOALS = [
  { id: 'lower-androgens', label: 'Lower androgens' },
  { id: 'decrease-gc-dose', label: 'Decrease GC dose' },
];

export const GC_CALC_LAB_DRAW_TIMES = [
  { id: 'before-morning-gc', label: 'Before morning GC dose' },
  { id: 'after-morning-gc',  label: 'After morning GC dose' },
];

export const GC_CALC_A4_ASSESSMENTS = ['above-goal', 'at-goal-supraphysiologic', 'at-goal', 'below-goal'];

export const GC_CALC_WITHDRAWAL_SEVERITIES = ['adrenal-insufficiency', 'none', 'mild-moderate', 'severe'];

export const GC_CALC_DOWN_TITRATION_EXPERIENCE = [
  { id: 'first-time', label: 'This is my first time down-titrating their dose' },
  { id: 'down-titrated-before', label: 'I have down-titrated their dose before' },
];


